import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import formatTotalBalance from "../../../../../utils/formatTotalBalance";
import GraphHeader from "../../CommonComponents/GraphHeader";
import CommonLineGraph from "../../CommonComponents/CommonLineGraph";
import FecthingPage from "../../CommonComponents/FecthingPage";
import { creditAndDebitAmount } from "../../../../../redux/features/Settings/Statistics";
import CommonToast from "../../../../_utils/CommonToast";
import ICONS from "../../../../../assets/Icons";
import {
  errorcode,
  GlobalConfiguration,
  SettingsStatisticsConfiguration,
} from "../../../../../utils/configurationProvider";
import globalText from "../../../../../utils/globalText";
import { toCamelCase } from "../../../../../utils/toCamelCase";

/**
 * DebitCreditAmount - A component that displays debit and credit amount trends over time
 * using a dual-line graph. Shows daily credit/debit amounts with totals. Supports filtering
 * by time period and amount unit.
 *
 * @component
 * @param {Object} props - Component props
 * @param {string} props.flow - Display flow: 'homeDashboard' or other
 * @param {boolean} [props.autoFetch=false] - Whether to fetch data automatically on mount
 * @param {Function} props.toggleAddedToHome - Callback to toggle widget on home dashboard
 * @param {boolean} props.isAddedToHome - Whether this widget is added to home dashboard
 * @returns {JSX.Element} Rendered debit/credit amount graph component
 */
const DebitCreditAmount = ({
  flow,
  autoFetch = false,
  toggleAddedToHome,
  isAddedToHome,
}) => {
  /**
   * Default fallback data for line graph - used as initial state before API fetch.
   * @type {Object}
   * @property {number[]} defaultItemsSizeForXAxis - Array of indices for x-axis positions
   * @property {number[]} dataFirst - Credit amount line data
   * @property {number[]} dataSecond - Debit amount line data
   * @property {string[]} xAxisData - Date labels for x-axis
   * @property {number} creditAmount - Total credit amount
   * @property {number} debitAmount - Total debit amount
   */
  const DEFAULT_GRAPH_DATA = {
    // NOTE: This is default dummy data for graph - used as fallback before fetch
    defaultItemsSizeForXAxis: [0, 1, 2, 3, 4, 5, 6],
    dataFirst: [200, 500, 300, 100, 600, 800, 350],
    dataSecond: [100, 700, 200, 300, 500, 400, 250],
    xAxisData: [
      "2025-01-01",
      "2025-01-02",
      "2025-01-03",
      "2025-01-04",
      "2025-01-05",
      "2025-01-06",
      "2025-01-07",
    ],
    creditAmount: 1001222000,
    debitAmount: 40122200000,
  };

  // Graph data state - initialized with default dummy data
  const [graphData, setGraphData] = useState(DEFAULT_GRAPH_DATA);

  // Time period filter state (defaults to last 7 days)
  const [selectedDays, setSelectedDays] = useState(
    globalText?.LAST_7_DAYS.toLowerCase(),
  );

  // Amount unit filter state (e.g., "", "thousand", "lakh", "crore")
  const [selectedAmount, setSelectedAmount] = useState("");

  // Controls whether the fetch page UI is shown (vs auto-fetched data)
  const [fetchPageEnabled, setFetchPageEnabled] = useState(!autoFetch);

  // Loading state for manual fetch button
  const [fetchPageLoading, setFetchPagefetchPageLoading] = useState(false);

  // Error state for displaying error UI
  const [isError, setIsError] = useState(false);

  // Raw report data from API (stored for re-calculation when unit changes)
  const [rawReportData, setRawReportData] = useState(null);
  const toastShownRef = useRef(false);
  const services = useSelector((state) => state.refreshToken.services);
  const globalStatisticsService = services?.includes(
    globalText?.VIEW_STATISTICS,
  ); // Global service for statistics "VStatistics".
  const homeDashboard = toCamelCase(globalText?.HOME_DASHBOARD);

  const dispatch = useDispatch();
  const loginData = useSelector((state) => state.refreshToken.data);

  // Select loading and error states from Redux
  const { creditAndDebitAmountLoading, creditAndDebitAmountError } =
    useSelector((state) => state.settingStatistics);

  // Extract entity ID from login data if user is authenticated
  const entityId = loginData?.status ? loginData?.entity?.entity_id : "";

  /**
   * Transforms API report data into arrays suitable for dual-line graph rendering.
   * Handles amount formatting based on selected unit and extracts date labels.
   */
  const transformReportData = (reports, selectedAmount) => {
    return reports.reduce(
      (acc, val, index) => {
        // X-axis position index
        acc.defaultGraphDataItemsForXaxis.push(index);

        // Primary data: credit amounts (formatted if unit selected)
        acc.graphDataFirst.push(
          selectedAmount
            ? formatTotalBalance(val.credit, selectedAmount).amount
            : val.credit,
        );

        // Secondary data: debit amounts (formatted if unit selected)
        acc.graphDataSecond.push(
          selectedAmount
            ? formatTotalBalance(val.debit, selectedAmount).amount
            : val.debit,
        );

        // Extract date label - prefers 'day' property, falls back to 'date'
        if (Object.hasOwn(val, globalText?.DAY.toLowerCase())) {
          acc.xAxisLabels.push(val.day);
        } else {
          acc.xAxisLabels.push(val.date);
        }

        return acc;
      },
      {
        defaultGraphDataItemsForXaxis: [],
        graphDataFirst: [],
        graphDataSecond: [],
        xAxisLabels: [],
      },
    );
  };

  /**
   * Processes API response and updates component state with credit/debit data.
   * On success: transforms data, shows success toast (once), updates graph data.
   * On failure: resets to default data, conditionally enables fetch page, sets error.
   */
  const handleCalculation = useCallback(
    (response) => {
      const reportData = response?.payload?.report_data;

      if (response?.payload?.status && reportData) {
        // Store raw data for re-calculation when amount unit changes
        setRawReportData(reportData);

        // Transform report data for graph
        const {
          defaultGraphDataItemsForXaxis,
          graphDataFirst,
          graphDataSecond,
          xAxisLabels,
        } = transformReportData(
          reportData.generate_reports || [],
          selectedAmount,
        );

        // Update graph data with transformed arrays and summary totals
        setGraphData({
          defaultItemsSizeForXAxis: defaultGraphDataItemsForXaxis,
          dataFirst: graphDataFirst,
          dataSecond: graphDataSecond,
          xAxisData: xAxisLabels,
          creditAmount: reportData.total_credit_amount,
          debitAmount: reportData.total_debit_amount,
        });
        // toast shows only once
        if (!toastShownRef.current && fetchPageEnabled) {
          CommonToast({
            text: globalText?.FETCHED_SUCCESSFULLY, //"Fetched successfully.",
            icon: ICONS?.SELECT_ALL_WITH_WHITE_FILLED_CIRCLE,
            variant: "var(--success)",
            btnProps: {
              isDisabled: false,
              enabledTextColor: "var(--text-primary)",
              enabledBgColor: "var(--primary-bg)",
            },
          });
          toastShownRef.current = true;
        }
        setFetchPageEnabled(false);
      } else {
        // fallback to defaults
        setGraphData(DEFAULT_GRAPH_DATA);
        // Enable fetch page unless it's a "not found" error (no data exists)
        setFetchPageEnabled(
          response?.payload?.status !== errorcode?.errorNotFound,
        );
        toastShownRef.current = false;
        setIsError(true);
      }
    },
    [selectedDays, entityId],
  );

  /**
   * Fetches credit/debit amount data from API and updates component state.
   * Requires valid entity ID.
   */
  const fetchData = async () => {
    // Guard: no entity ID
    if (!entityId) return;
    setIsError(false);

    const response = await dispatch(
      creditAndDebitAmount({ entity_id: entityId, period: selectedDays }),
    );
    setFetchPagefetchPageLoading(false);
    handleCalculation(response);
  };

  /**
   * Handler for manual "Fetch Data" button click.
   * Initiates fetch by enabling loading and disabling fetch page.
   */
  const handleFetchClick = () => {
    setFetchPagefetchPageLoading(true);
    setFetchPageEnabled(false);
  };

  /**
   * Effect: Trigger data fetch when autoFetch is enabled or fetch page is disabled.
   * Re-runs when autoFetch, fetchPageEnabled, or selectedDays changes.
   */
  useEffect(() => {
    if (!fetchPageEnabled || autoFetch) {
      fetchData();
    }
  }, [autoFetch, fetchPageEnabled, selectedDays]);

  /**
   * Effect: Recalculate graph data when only the amount unit changes.
   * Uses cached rawReportData to avoid re-fetching from API.
   * Re-runs when selectedAmount or rawReportData changes.
   */
  useEffect(() => {
    if (rawReportData) {
      const {
        defaultGraphDataItemsForXaxis,
        graphDataFirst,
        graphDataSecond,
        xAxisLabels,
      } = transformReportData(
        rawReportData.generate_reports || [],
        selectedAmount,
      );

      setGraphData({
        defaultItemsSizeForXAxis: defaultGraphDataItemsForXaxis,
        dataFirst: graphDataFirst,
        dataSecond: graphDataSecond,
        xAxisData: xAxisLabels,
        creditAmount: rawReportData.total_credit_amount,
        debitAmount: rawReportData.total_debit_amount,
      });
    }
  }, [selectedAmount, rawReportData]);

  return (
    <div
      className={`w-width-full flex flex-col bg-primary-card ${flow === homeDashboard ? "rounded-b-border-radius-md" : "rounded-border-radius-md"}`}
    >
      {/* Header with title, period filter, amount unit filter, and add-to-home toggle */}
      <GraphHeader
        handlingOptionsRequired={[
          {
            type: `${globalText?.DAY.toLowerCase()}s`,
            setterState: setSelectedDays,
          },
          {
            type: `${globalText?.AMOUNT.toLowerCase()}s`,
            setterState: setSelectedAmount,
          },
        ]}
        flow={flow}
        autoFetch={autoFetch}
        titleText={globalText?.DEBIT_AND_CREDIT}
        hideTitle={flow === homeDashboard ? true : false}
        isAddedToHome={isAddedToHome}
        toggleAddedToHome={toggleAddedToHome}
      />
      <div className="flex flex-col relative">
        {/* Fetch/Loading/Error state page */}
        <FecthingPage
          fetchPageEnabled={
            (!autoFetch && fetchPageEnabled) || !globalStatisticsService
          }
          loading={creditAndDebitAmountLoading || fetchPageLoading}
          isError={isError}
          errorData={
            creditAndDebitAmountError &&
            creditAndDebitAmountError === errorcode?.errorNotFound
              ? { status: errorcode?.errorNotFound }
              : null
          }
          handleFunction={handleFetchClick}
        />

        {/* Line Graph Container with horizontal scrolling for many data points */}
        <div className="w-width-full pb-padding-sm relative overflow-x-auto ">
          {/* Dynamic minimum width based on data points count for proper spacing */}
          <div
            style={{
              minWidth: `${Math.max((graphData.xAxisData?.length || GlobalConfiguration?.lengthCheckZero) * SettingsStatisticsConfiguration?.graphMinWidthPerDataPoint)}px`,
            }}
          >
            <CommonLineGraph
              graphDataFirst={graphData.dataFirst}
              graphDataSecond={graphData.dataSecond}
              xAxisLabels={graphData.xAxisData}
              graphDataFirstColor={"var(--settled)"}
              graphDataSecondColor={"var(--queued)"}
              defaultDataItemsForXaxis={graphData.defaultItemsSizeForXAxis}
              amountFormatForLegend={selectedAmount}
              period={selectedDays}
            />
          </div>
        </div>

        {/* Footer with Credit/Debit totals */}
        <div className="w-width-full bg-primary-nav rounded-b-border-radius-md">
          <div className="flex Qsmd:flex-row flex-col gap-gap-sm py-padding-sm px-padding-md">
            {/* Credit Amount Card */}
            <div className="flex gap-gap-md items-center Qsmd:flex-1">
              <div className="text-text-secondary text-font-xxs">
                Credit amount :
              </div>
              <div className="text-positive-act text-font-xs font-semibold">
                {fetchPageEnabled
                  ? "-"
                  : "INR " +
                    formatTotalBalance(
                      graphData.creditAmount,
                      selectedAmount === ""
                        ? globalText?.AMOUNT.toLowerCase() //"amount"
                        : selectedAmount,
                    ).amount +
                    " " +
                    selectedAmount}
              </div>
            </div>

            {/* Debit Amount Card - bordered separator on desktop */}
            <div className="flex Qsmd:flex-1 gap-gap-md items-center Qsmd:border-l-border Qsmd:border-t-border-base Qsmd:pl-padding-md border-t-border Qsmd:border-l-primary-bg border-t-primary-bg Qsmd:pt-0 pt-padding-sm ">
              <div className="text-text-secondary text-font-xxs">
                Debit amount :
              </div>
              <div className="text-queued text-font-xs font-semibold">
                {fetchPageEnabled
                  ? "-"
                  : "INR " +
                    formatTotalBalance(
                      graphData.debitAmount,
                      selectedAmount === ""
                        ? globalText?.AMOUNT.toLowerCase() //"amount"
                        : selectedAmount,
                    ).amount +
                    " " +
                    selectedAmount}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DebitCreditAmount;
